import { type KeyboardEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components/Icon';
import { cn } from '~/lib/cn';

export interface ChatPrompterProps {
  isLoading?: boolean;
  onSubmit?: (question: string) => void;
  onStop?: () => void;
  onOpenModalClick?: () => void;
}

export function ChatPrompter({
  isLoading,
  onSubmit,
  onStop,
  onOpenModalClick,
}: ChatPrompterProps) {
  const { t } = useTranslation('data', {
    keyPrefix: 'musicIq.chatPrompter',
  });
  const [inputValue, setInputValue] = useState('');

  const handleSubmit = useCallback(() => {
    const trimmedValue = inputValue.trim();

    if (trimmedValue && !isLoading) {
      onSubmit?.(trimmedValue);
    }
  }, [inputValue, isLoading, onSubmit]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleButtonClick = useCallback(() => {
    if (isLoading) {
      onStop?.();
    } else {
      handleSubmit();
    }
  }, [isLoading, onStop, handleSubmit]);

  const hasInput = inputValue.trim().length > 0;
  const isSubmitDisabled = !hasInput && !isLoading;

  return (
    <div
      className="flex flex-col gap-md rounded-sm border px-lg py-md bg-level-02 border-emphasis-subtle"
      role="form"
      aria-label={t('inputPlaceholder')}
    >
      <div className="flex items-center gap-sm">
        <div
          className={cn('rounded-sm', isLoading && 'p-[1px]')}
          // Add border gradient
          style={
            isLoading
              ? {
                  background:
                    'linear-gradient(to right, oklch(var(--bg-tertiary-default)), oklch(var(--bg-primary-default)))',
                }
              : undefined
          }
        >
          <div
            className={cn(
              'flex items-center gap-md',
              'h-[42px] w-[386px] rounded-sm p-md',
              isLoading
                ? 'bg-level-02'
                : 'border bg-emphasis-default border-emphasis-subtle',
            )}
          >
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('inputPlaceholder')}
              disabled={isLoading}
              className={cn(
                'flex-1 text-body-sm outline-none bg-transparent text-default',
                'placeholder:text-subtle',
                'disabled:cursor-not-allowed',
              )}
              aria-label={t('inputPlaceholder')}
            />
          </div>
        </div>

        <button
          type="button"
          onClick={handleButtonClick}
          disabled={isSubmitDisabled}
          className={cn(
            'size-[42px] rounded-sm',
            'text-default',
            (isLoading || hasInput) &&
              'border-2xs bg-emphasis-default border-emphasis-subtle',
            'disabled:cursor-not-allowed disabled:bg-emphasis-default disabled:text-disabled',
          )}
          aria-label={isLoading ? t('stopAriaLabel') : t('submitAriaLabel')}
        >
          {isLoading ? (
            <Icon type="circle-stop" variant="solid" size="md" />
          ) : (
            <Icon type="arrow-up" variant="solid" size="md" />
          )}
        </button>
      </div>

      {/* Disclaimer */}
      <div className="flex gap-x-xs text-left text-caption-md font-semibold text-info-default">
        {t('disclaimer')}
        <button
          type="button"
          onClick={onOpenModalClick}
          className="font-semibold underline text-info-default hover:text-info-hover"
        >
          {t('learnMore')}
        </button>
      </div>
    </div>
  );
}

ChatPrompter.displayName = 'ChatPrompter';
